import { useCallback, useMemo, useRef, useState } from "react"
import { CaretDown, CaretRight, Warning, X } from "@phosphor-icons/react"

import PhasingOutAnswer, {
  sumPhasingOutFigures,
} from "~/components/demand-2/demand/PhasingOutAnswer"
import {
  Badge,
  Button,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  IconButton,
  StatRow,
  Switch,
} from "~/components/design-system/components"
import SuperChart, { type StackedBarChartData } from "~/components/reusable/graphs/SuperChart"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import { getTailwindValue } from "~/utils/tailwind"
import { SizingSpinner } from "~/components/demand-2/shared/SizingSpinner"
import { DeltaCell } from "~/components/demand-2/shared/demandBreakdownShared"
import SeasonTrendBreakdown from "~/components/demand-2/demand/SeasonTrendBreakdown"
import GrowthLandingTable, {
  growthLandingOf,
} from "~/components/demand-2/demand/GrowthLandingTable"
import SeasonTrendAnswer, {
  unconfirmedGrowthBase,
} from "~/components/demand-2/demand/SeasonTrendAnswer"
import DiscountsBreakdown, {
  sumDiscountFigures,
} from "~/components/demand-2/demand/DiscountsBreakdown"
import DiscountsAnswer from "~/components/demand-2/demand/DiscountsAnswer"
import StockoutsBreakdown, {
  sumStockoutFigures,
} from "~/components/demand-2/demand/StockoutsBreakdown"
import StockoutsAnswer from "~/components/demand-2/demand/StockoutsAnswer"
import PreorderBreakdown from "~/components/demand-2/demand/PreorderBreakdown"
import { UnmeasuredNote } from "~/components/demand-2/demand/FashionPreorderDevelopmentBreakdown"
import type { CollectionKind } from "~/utils/collectionKind"
import {
  usePublishSetupSummary,
  useSetupSummaryStore,
  type InsightRow,
  type SetupSummary,
} from "~/components/demand-2/demand/setupSummary"
import { stackDeltas } from "~/components/demand-2/demand/setupWaterfall"
import {
  INSIGHT_GROUP_TITLE,
  STRATEGIC_ADJUSTMENTS_TITLE,
  STRATEGY_GROUP_TITLE,
} from "~/components/demand-2/shared/adjustmentBadges"
import type { AdjustmentImpact } from "~/components/demand-2/shared/adjustmentImpacts"
import {
  insightRequirementFor,
  levelSliceDetail,
  type NoosMetricKey,
} from "~/components/demand-2/shared/noosBreakdown"
import { useClearVariantFramingAnswers } from "~/components/demand-2/shared/FramingAnswerPicker"
import { useOpenInTotal } from "~/components/demand-2/shared/useOpenInTotal"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { insightAnswerLabel } from "~/components/demand-2/shared/insightAnswerLabel"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useDemandHierarchy, useLevelLabel } from "~/components/demand-2/useDemandHierarchy"
import {
  useAdjustmentQuestions,
  useAllFramingAnswers,
  useCollectionInsights,
  useFraming,
  useFramingAnswers,
  useNodeInsights,
  useSeasonTrendMode,
} from "~/components/demand-2/useDemandIntents"
import {
  type AnswerScope,
  canAnswerPerSlice,
  effectiveAnswerAt,
  ownVariantAnswers,
  effectiveAnswerFor,
  type FramingSlice,
  type SliceAnswerQuestion,
  sliceKey,
  type FramingAnswerRow,
  type FramingNode,
  type FramingQuestionId,
  type FramingResolution,
  framingOptionLabel,
  storedFramingAnswer,
} from "~/utils/demandFraming"
import { isFramingSignalKey } from "~/utils/demandFramingSignal"
import { modeUnits } from "~/utils/demandSeasonTrendMode"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { groupingAttributeOf, isAssortmentLevel, UNCATEGORIZED } from "~/utils/demandHierarchy"
import { scaleInsightScalars } from "~/utils/demandInsightSlice"
import { useSalesSlices } from "~/components/demand-2/shared/useSalesSlices"
import { SliceAnswerList } from "~/components/demand-2/shared/SliceAnswerLine"
import { api } from "~/utils/api"
import {
  effectiveKeysAtNode,
  type InsightNode,
  nodeAnswerFor,
  type NodeInsightRow,
} from "~/utils/demandInsightNodes"
import { sumNodeTrend } from "~/utils/demandSalesTrend"
import {
  preorderRowFigures,
  unmeasuredNote,
  useFashionPreorderDevelopment,
  usePreorderDevelopment,
} from "~/components/demand-2/shared/preorderDevelopment"
import {
  EXCLUSIVE_SLOT_ANSWERS,
  INSIGHT_KEY_ORDER,
  INSIGHT_KEY_SLOT,
  type InsightSlot,
  isStrategicInsightKey,
  LEVEL_SCOPED_INSIGHT_KEYS,
} from "~/utils/demandInsightValues"
import { formatNumber } from "~/utils/formatting"
import { cn, roundNumber } from "~/utils/helpers"
import { STRATEGY_REQUIRES } from "~/components/demand-2/shared/strategyPermission"
import { LevelDisclosure } from "~/components/demand-2/shared/LevelDisclosure"
import {
  NOT_ANSWERED,
  PctBadge,
  STRATEGY_CARD_HEADER_CLASS,
  StrategyImpactColumns,
} from "~/components/demand-2/shared/StrategyImpactColumns"
import { CARD_OPEN_MS, STRATEGY_TARGET_ATTR } from "~/components/demand-2/shared/strategyTarget"
import { keepInPlace } from "~/utils/keepInPlace"
import type { StrategySectionKey } from "~/components/demand-2/shared/StrategySummaryRail"

/**
 * Which insights every variant in the collection carries. The switches write one row per key and
 * the engine sizes each variant at read time, so variants added later inherit the setting.
 * `BulkSetupInsightsCard` is the per-variant counterpart: it freezes values on existing variants.
 */

/** Rows that state their own rate vs their own prior year; a baseline-share badge would clash. */
const NATIVE_PCT_KEYS = new Set<NoosMetricKey>(["salesTrend", "seasonTrend", "preorderDevelopment"])

const EXPLAINED_KEYS = new Set<NoosMetricKey>([
  "salesTrend",
  "seasonTrend",
  "preorderDevelopment",
  "discounts",
  "stockout",
  "phasingOut",
])

/** Whether the row opens its own breakdown instead of the contribution chart. */
const isExplained = (key: NoosMetricKey): boolean => EXPLAINED_KEYS.has(key)

/** The framing question a row's nodes answer; null means the nodes are a read-only split. */
const ROW_QUESTIONS: ReadonlySet<string> = new Set<FramingQuestionId>([
  "seasonTrend",
  "discounts",
  "stockout",
  "preorderDevelopment",
  "phasingOut",
])

const questionForRow = (key: NoosMetricKey): FramingQuestionId | null =>
  ROW_QUESTIONS.has(key) ? (key as FramingQuestionId) : null

/** By slot, never per key: two answers to the same question share a colour. */
const SLOT_DOT: Record<InsightSlot, string> = {
  growth: "bg-badge-green-fg-text",
  preorder: "bg-badge-blue-fg-text",
  unrealised: "bg-badge-orange-fg-text",
  pricing: "bg-badge-violet-fg-text",
  lifecycle: "bg-badge-red-fg-text",
  plannerSignals: "bg-fg-interactive",
}

/** The line under an insight's label: requirement, then row note, rate source, overrides. */
const insightNoteFor = ({
  requirement,
  rowNote,
  key,
  seasonRange,
  overridden,
}: {
  requirement: string | null
  rowNote?: string
  key: NoosMetricKey
  seasonRange: string | null
  overridden: number
}): string | null => {
  if (requirement) return requirement
  if (rowNote) return rowNote
  if (key === "seasonTrend") return seasonRange
  if (overridden > 0) return `${formatNumber(overridden)} variants keep their own accepted value`
  return null
}

/** The two seasons the lineage rate was measured over, named from the collection group. */
const seasonRangeNote = (season: {
  year1: number
  year2: number
  comparable: { names: string[] } | null
  prior: { names: string[] } | null
}): string | null => {
  const from = season.prior?.names.join(", ")
  const to = season.comparable?.names.join(", ")
  if (!from || !to) return null
  return `${from} ${formatNumber(Math.round(season.year2))} → ${to} ${formatNumber(Math.round(season.year1))}`
}

const CONTRIBUTION_COLUMNS = 12

export const deltaTone = (units: number | null | undefined): "good" | "warn" | undefined => {
  if (units == null || Math.round(units) === 0) return undefined
  return units > 0 ? "good" : "warn"
}

/** A per-variant row on several variants names the count, since its units sum over them. */
const signalRowLabel = (row: AdjustmentImpact): string => {
  const n = row.variantCount ?? 1
  if (row.collectionWide || n <= 1) return row.name
  return `${row.name} · ${formatNumber(n)} variants`
}

export type SignalComponent = {
  key: string
  label: string
  dot: string
  units: number | null
}

type SignalGroup = {
  title: string
  /** Applied units only; the groups partition the stage's delta with no remainder. */
  units: number
  components: SignalComponent[]
}

/**
 * The Demand signals delta split into Model insights, then Strategy. The Strategy
 * group keeps the engine's fold order (Cannibalization, comparable-season growth, setup rows) and
 * closes with whatever no row accounts for, so its subtotal reconciles. Empty groups are dropped.
 */
const signalGroups = ({
  appliedRows,
  strategicRows,
  signalRows,
  strategicUnits,
  insightUnits,
}: {
  appliedRows: SetupSummary["appliedRows"]
  strategicRows: SetupSummary["strategicRows"]
  signalRows: SetupSummary["signalRows"]
  strategicUnits: number
  insightUnits: number
}): SignalGroup[] => {
  const plannerRow = (row: SetupSummary["signalRows"][number]): SignalComponent => ({
    key: row.id,
    label: signalRowLabel(row),
    dot: SLOT_DOT.plannerSignals,
    units: row.units,
  })
  const strategic: SignalComponent[] = [
    ...signalRows.filter((row) => isFramingSignalKey(row.id)).map(plannerRow),
    ...strategicRows.map((row) => ({
      key: row.key,
      label: row.label,
      dot: SLOT_DOT[INSIGHT_KEY_SLOT[row.key]],
      units: row.units,
    })),
    ...signalRows.filter((row) => !isFramingSignalKey(row.id)).map(plannerRow),
  ]
  const unattributedSetup =
    strategicUnits -
    signalRows.reduce((sum, r) => sum + (r.units ?? 0), 0) -
    strategicRows.reduce((sum, r) => sum + r.units, 0)
  if (Math.round(unattributedSetup) !== 0) {
    strategic.push({
      key: "unattributed",
      label: signalRows.length > 0 ? "Other strategic adjustments" : STRATEGIC_ADJUSTMENTS_TITLE,
      dot: SLOT_DOT.plannerSignals,
      units: unattributedSetup,
    })
  }
  // In the Insights card's own row order (`INSIGHT_KEY_ORDER`), so the list reads down the page.
  const insights: SignalComponent[] = appliedRows
    .filter((row) => !isStrategicInsightKey(row.key))
    .sort((a, b) => INSIGHT_KEY_ORDER.indexOf(a.key) - INSIGHT_KEY_ORDER.indexOf(b.key))
    .map((row) => ({
      key: row.key,
      label: row.label,
      dot: SLOT_DOT[INSIGHT_KEY_SLOT[row.key]],
      units: row.units,
    }))
  return [
    { title: INSIGHT_GROUP_TITLE, units: insightUnits, components: insights },
    { title: STRATEGY_GROUP_TITLE, units: strategicUnits, components: strategic },
  ].filter((group) => group.components.length > 0)
}

/**
 * The Demand signals split the strategy rail reads, off the same derivation the waterfall bars use
 * (`stackDeltas`), so a row and its bar cannot disagree.
 */
export const strategySignalComponents = (summary: SetupSummary) => {
  const { stages, appliedRows, strategicRows, signalRows } = summary
  const { setupAdjustments: strategicUnits, insightUnits } = stackDeltas(stages, appliedRows)
  const groups = signalGroups({
    appliedRows,
    strategicRows,
    signalRows,
    strategicUnits,
    insightUnits,
  })
  const componentsOf = (title: string) => groups.find((g) => g.title === title)?.components ?? []
  const strategicKeys = new Set<string>(strategicRows.map((r) => r.key))
  const strategic = componentsOf(STRATEGY_GROUP_TITLE)
  return {
    insights: componentsOf(INSIGHT_GROUP_TITLE),
    assortment: strategic.filter((c) => isFramingSignalKey(c.key)),
    demand: strategic.filter((c) => strategicKeys.has(c.key)),
    otherSetup: strategic.filter((c) => !isFramingSignalKey(c.key) && !strategicKeys.has(c.key)),
  }
}

/** The variants an insight is worth most on, off the same per-variant offers the row total sums. */
const ContributionChart = ({ row }: { row: InsightRow }) => {
  const top = row.contributions.slice(0, CONTRIBUTION_COLUMNS)
  const data: StackedBarChartData[] = [
    {
      name: row.label,
      type: "column",
      color: getTailwindValue("--color-purpleheart-500"),
      data: top.map((c) => Math.round(c.units)),
    },
  ]
  return (
    <div className="flex flex-col gap-2">
      <span className="label-sm text-fg-base">
        {row.contributions.length > CONTRIBUTION_COLUMNS
          ? `Top ${CONTRIBUTION_COLUMNS} of ${formatNumber(row.contributions.length)} variants`
          : "Per variant"}
      </span>
      <SuperChart
        type={ChartType.StackedColumn}
        data={data}
        xCategories={top.map((c) => c.label)}
        height={240}
        legendAlignment={LegendAlignment.Bottom}
      />
    </div>
  )
}

/** One fold feeds both chart and picker, so they cannot quote different figures. */
const DiscountsDetail = ({
  variants,
  summary,
  node,
}: {
  variants: readonly IVariantSummary[]
  summary: SetupSummary
  node?: FramingNode
}) => {
  const figures = sumDiscountFigures(variants, summary.scalarsFor)
  return (
    <div className="flex flex-col gap-3">
      <DiscountsBreakdown figures={figures} />
      <DiscountsAnswer node={node} figures={figures} />
    </div>
  )
}

/** One fold feeds both chart and picker, so they cannot quote different figures. */
const StockoutsDetail = ({
  variants,
  summary,
  node,
}: {
  variants: readonly IVariantSummary[]
  summary: SetupSummary
  node?: FramingNode
}) => {
  const figures = sumStockoutFigures(variants, summary.scalarsFor)
  return (
    <div className="flex flex-col gap-3">
      <StockoutsBreakdown figures={figures} />
      <StockoutsAnswer node={node} figures={figures} />
    </div>
  )
}

/** The variants the sell-down lands on, then the answer. */
const PhasingOutDetail = ({
  variants,
  summary,
  row,
  node,
}: {
  variants: readonly IVariantSummary[]
  summary: SetupSummary
  row: InsightRow
  node?: FramingNode
}) => {
  const figures = sumPhasingOutFigures(variants, summary.scalarsFor)
  const ids = new Set(variants.map((v) => v.variantId))
  const contributions = row.contributions.filter((c) => ids.has(c.variantId))
  return (
    <div className="flex flex-col gap-3">
      {contributions.length > 0 && <ContributionChart row={{ ...row, contributions }} />}
      <PhasingOutAnswer node={node} figures={figures} />
    </div>
  )
}

/** Nothing until the offers read carries the growth parts. */
const GrowthLanding = ({
  variants,
  summary,
}: {
  variants: readonly IVariantSummary[]
  summary: SetupSummary
}) => {
  const landing = growthLandingOf(variants, summary.scalarsFor, summary.effective)
  return landing && <GrowthLandingTable landing={landing} />
}

/**
 * The body under an insight's row. Derived rows show the breakdown that reconstructs their rate
 * instead of the contribution chart.
 */
const InsightDetailBody = ({ summary, row }: { summary: SetupSummary; row: InsightRow }) => {
  const { variants, perVariant, preorder } = summary
  const overridden = variants.filter((v) => perVariant.appliedFor(v.variantId).has(row.key)).length
  const unconfirmed = unconfirmedGrowthBase(variants, summary.scalarsFor)
  return (
    <div className="flex flex-col gap-3">
      {row.key === "seasonTrend" && (
        <div className="flex flex-col gap-3">
          <SeasonTrendBreakdown unconfirmed={unconfirmed} />
          <GrowthLanding variants={variants} summary={summary} />
          <SeasonTrendAnswer unconfirmed={unconfirmed} />
        </div>
      )}
      {row.key === "discounts" && <DiscountsDetail variants={variants} summary={summary} />}
      {row.key === "stockout" && <StockoutsDetail variants={variants} summary={summary} />}
      {row.key === "phasingOut" && (
        <PhasingOutDetail variants={variants} summary={summary} row={row} />
      )}
      {overridden > 0 && (
        <StatRow
          label="Keep their own value"
          value={`${formatNumber(overridden)} variants`}
          weight="muted"
        />
      )}
      {row.key === "preorderDevelopment" && (
        <div className="border-t border-base pt-3">
          <PreorderBreakdown persistKey="demand2-preorder-development-breakdown" data={preorder} />
        </div>
      )}
      {!isExplained(row.key) && row.contributions.length > 0 && (
        <div className="border-t border-base pt-3">
          <ContributionChart row={row} />
        </div>
      )}
    </div>
  )
}

/** `undefined` while loading or where the collection has no lineage to measure on. */
const growthAnswerLabel = (
  strategyQuestions: readonly FramingQuestionId[] | undefined,
  answers: readonly FramingAnswerRow[],
): string | undefined => {
  if (strategyQuestions == null || !strategyQuestions.includes("seasonTrend")) return undefined
  const stored = storedFramingAnswer(answers, "seasonTrend")
  if (stored == null) return NOT_ANSWERED
  // "Answered" only for a stored option this build cannot read.
  return framingOptionLabel("seasonTrend", stored) ?? "Answered"
}

/** The row's own rate badge, in percent; only season trend and pre-order carry one. */
const rowTrendPct = (
  key: NoosMetricKey,
  p: {
    seasonAppliedPct: number | null | undefined
    preorderVsBasisPct: number | null | undefined
  },
): number | null => {
  if (key === "seasonTrend") return p.seasonAppliedPct ?? null
  if (key === "preorderDevelopment" && p.preorderVsBasisPct != null)
    return p.preorderVsBasisPct * 100
  return null
}

/** `undefined` for a row with no question here, or while the questions load. */
const rowAnswerLabel = (
  question: FramingQuestionId | null,
  questions: readonly FramingQuestionId[] | undefined,
  answers: readonly FramingAnswerRow[],
  kind: CollectionKind,
): string | undefined => {
  if (question == null || !questions?.includes(question)) return undefined
  const stored = storedFramingAnswer(answers, question)
  if (stored == null) return NOT_ANSWERED
  return (
    insightAnswerLabel(
      question,
      { answer: stored, inherited: false, isDefault: false },
      { kind },
    ) ?? "Answered"
  )
}

/** Shared by the Insights card and the growth card so both render a row from the same numbers. */
const useInsightRowContext = (summary: SetupSummary) => {
  const { parentGroup, collectionName, groupName } = useDemand2Scope()
  // A sales level splits a variant, not the collection, so only assortment levels answer per node.
  const { levels } = useDemandHierarchy()
  const groupingLevels = levels.filter(isAssortmentLevel)
  // In the store, not per row, so the open row survives `sizing` swapping rows for spinners.
  const expanded = useSetupSummaryStore((s) => s.expanded)
  const setExpanded = useSetupSummaryStore((s) => s.setExpanded)
  const {
    variants,
    configurableRows,
    baselineTotal,
    levelAuthorable,
    levelName,
    kind,
    seasonTrend,
    collection,
    perVariant,
    preorder,
    sizing,
  } = summary

  // The more specific per-variant row wins in the engine, so these escape the switch.
  const overriddenCountFor = (key: NoosMetricKey) =>
    variants.filter((v) => perVariant.appliedFor(v.variantId).has(key)).length

  const preorderVsBasisPct = preorderRowFigures(preorder).vsBasisPct
  // The applied rate, not the measured: they differ under "Match unit change" or a custom trend.
  const seasonMode = useSeasonTrendMode()
  const seasonAppliedPct = (): number | null => {
    if (seasonTrend.year1 <= 0) return seasonTrend.pct
    const units = modeUnits(seasonMode, seasonTrend)
    return units == null ? null : (units / seasonTrend.year1) * 100
  }

  const baselineSharePctFor = (row: InsightRow) =>
    NATIVE_PCT_KEYS.has(row.key) || baselineTotal === 0
      ? null
      : roundNumber((row.units / baselineTotal) * 100, 1)

  // Collection-wide key plus a per-variant rival key in the same slot: different keys, so
  // more-specific-wins never fires and both apply (double count).
  const rivalOverrideCountFor = (key: NoosMetricKey) => {
    const rivals = EXCLUSIVE_SLOT_ANSWERS[INSIGHT_KEY_SLOT[key]]
      ?.filter((group) => !group.includes(key))
      .flat()
    if (!rivals?.length) return 0
    return variants.filter((v) => {
      const applied = perVariant.appliedFor(v.variantId)
      return rivals.some((k) => applied.has(k))
    }).length
  }

  // Both kinds' rows are always on (`alwaysOnInsightKeys`) and answered, never switched.
  const allFramingAnswers = useAllFramingAnswers()
  // Only variants in scope, so a group route neither double-counts nor counts departed ones.
  const framing = useFraming()
  const ownVariantIdsFor = (key: NoosMetricKey): string[] => {
    const question = questionForRow(key)
    if (question == null) return []
    const inScope = new Set(variants.map((v) => v.variantId))
    return ownVariantAnswers(framing)
      .filter((a) => a.question === question && inScope.has(a.variantId))
      .map((a) => a.variantId)
  }
  // Same query the planning process grades this card on, so the overview and the card agree.
  const adjustmentQuestions = useAdjustmentQuestions()
  const insightQuestions = adjustmentQuestions?.questions
  const growthAnswer = growthAnswerLabel(adjustmentQuestions?.strategyQuestions, allFramingAnswers)
  // Growth is a Strategy question, so it is not among `insightQuestions`.
  const answerFor = (key: NoosMetricKey) =>
    key === "seasonTrend"
      ? growthAnswer
      : rowAnswerLabel(questionForRow(key), insightQuestions, allFramingAnswers, kind)

  return {
    parentGroup,
    collectionName,
    groupName,
    groupingLevels,
    expanded,
    setExpanded,
    variants,
    configurableRows,
    baselineTotal,
    levelAuthorable,
    levelName,
    kind,
    seasonTrend,
    collection,
    sizing,
    overriddenCountFor,
    preorderVsBasisPct,
    seasonAppliedPct,
    baselineSharePctFor,
    rivalOverrideCountFor,
    ownVariantIdsFor,
    growthAnswer,
    answerFor,
  }
}

type InsightRowContext = ReturnType<typeof useInsightRowContext>

/** One insight as its own card. Shared by the Insights section and the growth card. */
const InsightRowCard = ({
  row,
  summary,
  ctx,
  section,
  collapsed = false,
}: {
  row: InsightRow
  summary: SetupSummary
  ctx: InsightRowContext
  section: StrategySectionKey
  /** Held shut whatever its stored open state. */
  collapsed?: boolean
}) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const {
    parentGroup,
    collectionName,
    groupName,
    groupingLevels,
    expanded,
    setExpanded,
    variants,
    baselineTotal,
    levelName,
    seasonTrend,
    sizing,
    overriddenCountFor,
    preorderVsBasisPct,
    seasonAppliedPct,
    baselineSharePctFor,
    rivalOverrideCountFor,
    ownVariantIdsFor,
    answerFor,
  } = ctx
  if (sizing)
    return <SizingSpinner className="h-[70px] w-full rounded-md border border-base bg-base" />
  const overridden = overriddenCountFor(row.key)
  const ownVariantIds = ownVariantIdsFor(row.key)
  const rivalOverridden = rivalOverrideCountFor(row.key)
  const sharePct = baselineSharePctFor(row)
  const unavailable = !row.available
  const unmeasured =
    unavailable && row.key === "preorderDevelopment" && summary.preorder.kind === "fashion"
      ? unmeasuredNote(summary.preorder.unmeasured, false)
      : null
  const requirement = unavailable
    ? insightRequirementFor(row.key, levelName, {
        unavailable: seasonTrend.unavailable,
        applicable: seasonTrend.applicable,
        thisName: collectionName ?? groupName,
        comparableName: seasonTrend.comparable?.names.join(", "),
        groupName: parentGroup?.name,
        running: [
          ...(seasonTrend.comparable?.running ?? []),
          ...(seasonTrend.prior?.running ?? []),
        ],
        priorName: seasonTrend.prior?.names.join(", "),
      })
    : null
  const note = insightNoteFor({
    requirement,
    rowNote: row.note,
    key: row.key,
    seasonRange: seasonRangeNote(seasonTrend),
    overridden,
  })
  const open = !collapsed && !unavailable && expanded[section] === row.key
  // Pre-order development's options state their own figures, so the level line would only repeat them.
  const levelDetail = row.levelUnits != null && row.key !== "preorderDevelopment" && (
    <span className="paragraph-sm text-fg-subtle">{levelSliceDetail(row.levelUnits)}</span>
  )
  const noteLine = note && <span className="paragraph-sm text-fg-muted">{note}</span>
  const ownBadge = ownVariantIds.length > 0 && (
    <OwnAnswersBadge
      question={questionForRow(row.key)}
      variantIds={ownVariantIds}
      total={variants.length}
    />
  )
  const trendPct = rowTrendPct(row.key, {
    seasonAppliedPct: seasonAppliedPct(),
    preorderVsBasisPct,
  })
  const trendBadge = trendPct != null && <PctBadge pct={roundNumber(trendPct, 1)} />
  const shareBadge = !row.unitsLoading && sharePct != null && sharePct !== 0 && (
    <PctBadge
      pct={sharePct}
      title={`${formatNumber(Math.round(row.units))} against this ${levelName}'s ${formatNumber(Math.round(baselineTotal))} baseline`}
    />
  )
  // Not auto-cleared: a settings switch must not delete frozen per-variant rows.
  const rivalWarning = rivalOverridden > 0 && (
    <div className="flex items-start gap-2 rounded-md border border-badge-orange-border bg-badge-orange-bg p-2">
      <Warning className="mt-0.5 size-4 shrink-0 text-badge-orange-fg-text" />
      <span className="paragraph-sm text-badge-orange-fg-text">
        {formatNumber(rivalOverridden)} variants individually accepted the other answer to this
        question, so those count it twice. Clear it on the variant to fix.
      </span>
    </div>
  )
  const detail = (
    <>
      <InsightDetailBody summary={summary} row={row} />
      {sliceQuestionFor(row.key) ? (
        <InsightSliceLevels row={row} summary={summary} question={sliceQuestionFor(row.key)!} />
      ) : (
        <InsightNodeLevels
          levels={groupingLevels}
          variants={variants}
          row={row}
          summary={summary}
          sizing={sizing}
        />
      )}
    </>
  )

  // With no insight to act on there is nothing to answer.
  const storedAnswer = answerFor(row.key)
  const answer = unavailable && storedAnswer === NOT_ANSWERED ? undefined : storedAnswer
  return (
    <ExpandableCard
      {...{ [STRATEGY_TARGET_ATTR]: row.key }}
      ref={cardRef}
      expanded={open}
      onExpandedChange={(next) => {
        if (unavailable) return
        // Opening folds the section's open card; one above would drag this one up.
        if (next && cardRef.current) keepInPlace(cardRef.current, CARD_OPEN_MS)
        setExpanded(section, row.key, next)
      }}
      className={cn("scroll-mt-6 shadow-none border border-base", unavailable && "opacity-60")}
    >
      <ExpandableCardHeader className={STRATEGY_CARD_HEADER_CLASS}>
        {/* Fixed-width answer and number columns, so they line up from card to card. The title
            wraps rather than truncates: those columns leave it little room. */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className={"flex min-w-0 flex-1 items-center gap-2"}>
            <h2 className="label-base min-w-0 text-pretty text-fg-base">{row.label}</h2>
          </div>
          <StrategyImpactColumns
            answer={answer}
            afterAnswer={!unavailable && ownBadge}
            badges={
              !unavailable && (
                <>
                  {trendBadge}
                  {shareBadge}
                </>
              )
            }
            units={unavailable ? null : row.units}
            unitsLoading={!unavailable && row.unitsLoading}
          />
        </div>
      </ExpandableCardHeader>
      {unavailable && (unmeasured || noteLine) && (
        <div className="bg-base px-4 py-3">
          {unmeasured ? <UnmeasuredNote text={unmeasured} /> : noteLine}
        </div>
      )}
      <ExpandableCardContent className="flex flex-col gap-4">
        {(levelDetail || noteLine) && (
          <div className="flex flex-col gap-0.5">
            {levelDetail}
            {noteLine}
          </div>
        )}
        {rivalWarning}
        {detail}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

const InsightSettings = ({ summary }: { summary: SetupSummary }) => {
  const ctx = useInsightRowContext(summary)
  const { configurableRows } = ctx
  return (
    <div className="flex flex-col gap-4">
      {configurableRows.map((row) => (
        <InsightRowCard key={row.key} row={row} summary={summary} ctx={ctx} section="insights" />
      ))}
    </div>
  )
}

/**
 * "Growth from comparable collections" as a Strategy card. Counted with the Strategic assumptions
 * (`STRATEGIC_INSIGHT_KEYS`). Fashion only: NOOS has no comparable season.
 */
export const GrowthStrategyCard = ({
  summary,
  collapsed,
}: {
  summary: SetupSummary
  collapsed?: boolean
}) => {
  const ctx = useInsightRowContext(summary)
  const row = summary.rows.find((r) => r.key === "seasonTrend")
  if (summary.kind !== "fashion" || !row) return null
  return (
    <InsightRowCard row={row} summary={summary} ctx={ctx} section="demand" collapsed={collapsed} />
  )
}

/** The uncategorised bucket is keyed `""`, like the server. */
const variantsByNode = (
  variants: readonly IVariantSummary[],
  levelKey: string,
): Map<string, IVariantSummary[]> => {
  const byNode = new Map<string, IVariantSummary[]>()
  for (const v of variants) {
    const value = groupingAttributeOf(v, levelKey) ?? ""
    const bucket = byNode.get(value)
    if (bucket) bucket.push(v)
    else byNode.set(value, [v])
  }
  return byNode
}

const unitsOn = (row: InsightRow, variantIds: ReadonlySet<string>): number =>
  row.contributions.reduce((sum, c) => (variantIds.has(c.variantId) ? sum + c.units : sum), 0)

type NodeLevelProps = {
  row: InsightRow
  summary: SetupSummary
  sizing: boolean
}

/**
 * A NOOS node's own on/off row (`DemandSetupInsightNode`). Absent on fashion and on level-scoped
 * keys, where the node follows the collection.
 */
type NodeSwitch = {
  /** Every node row on the plan, for every key. */
  rows: readonly NodeInsightRow[]
  collectionKeys: ReadonlySet<NoosMetricKey>
  setNode: (node: InsightNode, key: NoosMetricKey, enabled: boolean | null) => void
}

/**
 * One insight per node of every grouping level. A node follows the collection until it answers on
 * its own (the picker on a key with a question, its own switch on a NOOS key without one).
 */
const InsightNodeLevels = ({
  levels,
  variants,
  ...props
}: NodeLevelProps & { levels: readonly string[]; variants: readonly IVariantSummary[] }) => {
  const labelFor = useLevelLabel()
  const { row, summary } = props
  const question = questionForRow(row.key)
  const answers = useFramingAnswers(question)
  const nodeInsights = useNodeInsights()
  const collection = useCollectionInsights()
  const nodeSwitch: NodeSwitch | null =
    summary.kind === "noos" &&
    question == null &&
    summary.configurableRows.some((r) => r.key === row.key) &&
    !LEVEL_SCOPED_INSIGHT_KEYS.has(row.key)
      ? {
          rows: nodeInsights.rows,
          collectionKeys: collection.enabled,
          setNode: nodeInsights.setNode,
        }
      : null
  const seasonAnswerFor = (node: InsightNode) =>
    question == null ? null : effectiveAnswerAt(answers, question, { segment: "", ...node })
  const answersOnItsOwn = (node: InsightNode): boolean => {
    if (question != null) return !seasonAnswerFor(node)!.inherited
    if (nodeSwitch) return nodeAnswerFor(nodeSwitch.rows, node, row.key) != null
    return false
  }
  const canDiffer = question != null || nodeSwitch != null
  return (
    <>
      {levels.map((levelKey) => {
        const byNode = variantsByNode(variants, levelKey)
        // Biggest first; the uncategorised bucket last, since it cannot answer.
        const nodes = [...byNode.entries()].sort((a, b) => {
          if ((a[0] === "") !== (b[0] === "")) return a[0] === "" ? 1 : -1
          return b[1].length - a[1].length
        })
        if (nodes.length === 0) return null
        const own = nodes.filter(
          ([value]) => value !== "" && answersOnItsOwn({ groupKey: levelKey, groupValue: value }),
        ).length
        return (
          <LevelDisclosure
            key={levelKey}
            title={`By ${labelFor(levelKey)}`}
            summary={canDiffer ? ownAnswersSummary(own, nodes.length) : undefined}
          >
            <div className="divide-y divide-border-base overflow-hidden rounded-md border border-base bg-base">
              {nodes.map(([value, nodeVariants]) => (
                <InsightNodeLine
                  key={value}
                  node={{ groupKey: levelKey, groupValue: value }}
                  variants={nodeVariants}
                  question={question}
                  seasonAnswer={seasonAnswerFor({ groupKey: levelKey, groupValue: value })}
                  nodeSwitch={nodeSwitch}
                  {...props}
                />
              ))}
            </div>
          </LevelDisclosure>
        )
      })}
    </>
  )
}

/**
 * Insights describe how a variant sold, so they answer per sales level (channel, warehouse,
 * market); growth shapes the assortment and keeps its assortment nodes.
 */
const sliceQuestionFor = (key: NoosMetricKey): SliceAnswerQuestion | null => {
  const question = questionForRow(key)
  return question != null && canAnswerPerSlice(question) ? question : null
}

/**
 * One insight per combination of the planning hierarchy's sales levels. A slice follows the
 * collection until it answers on its own; nothing renders until a sales level is configured.
 */
const InsightSliceLevels = ({
  row,
  summary,
  question,
}: {
  row: InsightRow
  summary: SetupSummary
  question: SliceAnswerQuestion
}) => {
  const salesSlices = useSalesSlices()
  const framing = useFraming()
  if (salesSlices == null || salesSlices.slices.length === 0) return null
  return (
    <SliceAnswerList
      title={salesSlices.title}
      summary={ownAnswersSummary}
      question={question}
      slices={salesSlices.slices}
      resolutionFor={(slice) => effectiveAnswerFor(framing, question, { kind: "whole", slice })}
    >
      {({ slice }) => <SliceInsightBody slice={slice} row={row} summary={summary} />}
    </SliceAnswerList>
  )
}

/**
 * {@link InsightDetailBody} for one slice. A component so only an open slice pays for its filtered
 * totals read: each variant's figures are scaled to the slice's share of its baseline, the share
 * the engine lands the slice's answer on.
 */
const SliceInsightBody = ({
  slice,
  row,
  summary,
}: {
  slice: FramingSlice
  row: InsightRow
  summary: SetupSummary
}) => {
  const variantIds = useMemo(
    () => summary.variants.map((v) => v.variantId).sort(),
    [summary.variants],
  )
  const totals = api.demand2.getDemandTotals.useQuery(
    { collectionIds: summary.collectionIds, variantIds, ...slice },
    { enabled: variantIds.length > 0 },
  )
  const shareOf = useMemo(() => {
    const scoped = new Map((totals.data ?? []).map((t) => [t.variantId, t.baseline]))
    return (variantId: string): number => {
      const whole = summary.scalarsFor(variantId)?.baseline ?? 0
      return whole > 0 ? Math.min((scoped.get(variantId) ?? 0) / whole, 1) : 0
    }
  }, [totals.data, summary])
  const scope = useMemo((): AnswerScope => ({ kind: "whole", slice }), [slice])
  const preorderRow = row.key === "preorderDevelopment"
  const fashion = useFashionPreorderDevelopment(
    summary.collectionIds,
    preorderRow && summary.kind === "fashion" && totals.data != null,
    undefined,
    scope,
    shareOf,
  )
  const scalarsFor = useCallback(
    (variantId: string) => scaleInsightScalars(summary.scalarsFor(variantId), shareOf(variantId)),
    [summary, shareOf],
  )
  const noos = usePreorderDevelopment(
    preorderRow && summary.kind === "noos" ? variantIds : NO_VARIANTS,
    scalarsFor,
    scope,
  )
  if (totals.data == null) return <SizingSpinner className="w-24" />
  if (row.key === "discounts") {
    const figures = sumDiscountFigures(summary.variants, scalarsFor)
    return (
      <div className="flex flex-col gap-3">
        <DiscountsBreakdown figures={figures} />
        <DiscountsAnswer scope={scope} figures={figures} />
      </div>
    )
  }
  if (row.key === "stockout") {
    const figures = sumStockoutFigures(summary.variants, scalarsFor)
    return (
      <div className="flex flex-col gap-3">
        <StockoutsBreakdown figures={figures} />
        <StockoutsAnswer scope={scope} figures={figures} />
      </div>
    )
  }
  if (row.key === "phasingOut") {
    const figures = sumPhasingOutFigures(summary.variants, scalarsFor)
    return <PhasingOutAnswer scope={scope} figures={figures} />
  }
  return (
    <PreorderBreakdown
      persistKey={`demand2-${row.key}-slice-${sliceKey(slice)}`}
      data={summary.kind === "fashion" ? fashion : noos}
    />
  )
}

const ownAnswersSummary = (own: number, total: number): string =>
  own === 0
    ? "All follow the collection"
    : `${formatNumber(own)} of ${formatNumber(total)} answer on their own`

/**
 * Variants that answered for themselves (narrowest scope, so they beat collection and node). The
 * count opens Total filtered to them; the X sends them back to the collection's answer.
 */
const OwnAnswersBadge = ({
  question,
  variantIds,
  total,
}: {
  question: FramingQuestionId | null
  variantIds: readonly string[]
  total: number
}) => {
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0] ?? 0
  const clearAll = useClearVariantFramingAnswers(collectionId)
  const { showOwnAnswers } = useOpenInTotal()
  const n = formatNumber(variantIds.length)
  // Short, so the row keeps room for its label; the share is in the title.
  const label = variantIds.length === 1 ? "1 own answer" : `${n} own answers`
  return (
    // React bubbles the dialog's clicks through its portal to the row header, which toggles.
    <span
      className="flex shrink-0"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Badge variant="violet" size="xs" className="gap-0.5 pr-0.5">
        <Button
          variant="transparent"
          size="2xs"
          // The badge's own type (`label-sm`), so it reads like the answer badge beside it.
          className="label-sm! h-auto p-0 text-badge-violet-fg-text hover:bg-transparent hover:underline"
          title={`${formatNumber(variantIds.length)} of ${formatNumber(total)} variants answer on their own. Show them in Total`}
          disabled={question == null}
          onClick={() => question != null && showOwnAnswers(question)}
        >
          {label}
        </Button>
        {question != null && (
          <ConfirmationDialog
            requires={STRATEGY_REQUIRES}
            title="Follow the collection again?"
            description={
              variantIds.length === 1
                ? "1 variant stops using its own answer and follows the collection's again."
                : `${n} variants stop using their own answer and follow the collection's again.`
            }
            confirmText="Follow overall"
            loading={clearAll.isPending}
            onConfirm={() =>
              clearAll.mutate({ collectionId, question, variantIds: [...variantIds] })
            }
            trigger={
              <IconButton
                variant="transparent"
                size="2xs"
                className="size-4 shrink-0 p-0 hover:bg-badge-violet-border"
                aria-label="Have these variants follow the collection again"
                title="Have these variants follow the collection again"
              >
                <X className="size-3 fill-badge-violet-fg-text" />
              </IconButton>
            }
          />
        )}
      </Badge>
    </span>
  )
}

/**
 * A component so only an open node pays for the read. Both kinds' hooks are called; each gates
 * itself on the kind.
 */
const NodePreorderBreakdown = ({
  node,
  variantIds,
  collectionIds,
  kind,
  persistKey,
  scalarsFor,
}: {
  node: InsightNode
  variantIds: string[]
  collectionIds: number[]
  kind: CollectionKind
  persistKey: string
  scalarsFor: SetupSummary["scalarsFor"]
}) => {
  // Answers on the node, not the collection.
  const scope = useMemo((): AnswerScope => ({ kind: "node", node }), [node])
  const noos = usePreorderDevelopment(kind === "noos" ? variantIds : NO_VARIANTS, scalarsFor, scope)
  const fashion = useFashionPreorderDevelopment(
    collectionIds,
    kind === "fashion",
    variantIds,
    scope,
  )
  return <PreorderBreakdown persistKey={persistKey} data={kind === "fashion" ? fashion : noos} />
}

/** Stable empty list: an inline `[]` would disable the NOOS read by identity churn, not by intent. */
const NO_VARIANTS: string[] = []

/** {@link InsightDetailBody} for one node: same components, reading and answering on the node. */
const NodeInsightBody = ({
  node,
  variants,
  row,
  summary,
}: {
  node: InsightNode
  variants: readonly IVariantSummary[]
  row: InsightRow
  summary: SetupSummary
}) => {
  const framingNode = { segment: "" as const, ...node }
  const variantIds = variants.map((v) => v.variantId)
  const persistKey = `demand2-${row.key}-${node.groupKey}-${node.groupValue}`
  if (row.key === "seasonTrend") {
    const measured = sumNodeTrend(summary.seasonTrend.nodes, node)
    const unconfirmed = unconfirmedGrowthBase(variants, summary.scalarsFor)
    return (
      <>
        <SeasonTrendBreakdown node={framingNode} unconfirmed={unconfirmed} />
        <GrowthLanding variants={variants} summary={summary} />
        <SeasonTrendAnswer node={framingNode} measured={measured} unconfirmed={unconfirmed} />
      </>
    )
  }
  if (row.key === "discounts")
    return <DiscountsDetail variants={variants} summary={summary} node={framingNode} />
  if (row.key === "stockout")
    return <StockoutsDetail variants={variants} summary={summary} node={framingNode} />
  if (row.key === "phasingOut")
    return <PhasingOutDetail variants={variants} summary={summary} row={row} node={framingNode} />
  if (row.key === "preorderDevelopment")
    return (
      <NodePreorderBreakdown
        node={node}
        variantIds={variantIds}
        collectionIds={summary.collectionIds}
        kind={summary.kind}
        persistKey={persistKey}
        scalarsFor={summary.scalarsFor}
      />
    )
  const ids = new Set(variantIds)
  const contributions = row.contributions.filter((c) => ids.has(c.variantId))
  if (contributions.length === 0)
    return (
      <p className="paragraph-sm text-fg-subtle">Nothing to apply on this node&apos;s variants.</p>
    )
  return <ContributionChart row={{ ...row, contributions }} />
}

/** One node on one insight: a scan line while closed, the node's graph and input when open. */
const InsightNodeLine = ({
  node,
  variants,
  row,
  summary,
  sizing,
  question,
  seasonAnswer,
  nodeSwitch,
}: NodeLevelProps & {
  node: InsightNode
  variants: readonly IVariantSummary[]
  question: FramingQuestionId | null
  seasonAnswer: FramingResolution | null
  nodeSwitch: NodeSwitch | null
}) => {
  const [open, setOpen] = useState(false)
  // The uncategorised bucket is shown but cannot be answered.
  const answerable = node.groupValue !== ""
  const units = unitsOn(row, new Set(variants.map((v) => v.variantId)))
  const Caret = open ? CaretDown : CaretRight
  const answerLabel =
    seasonAnswer == null || question == null
      ? null
      : insightAnswerLabel(question, seasonAnswer, { kind: summary.kind, scope: "node" })
  const nodeOwn = nodeSwitch ? nodeAnswerFor(nodeSwitch.rows, node, row.key) : undefined
  const nodeOn = nodeSwitch
    ? effectiveKeysAtNode(nodeSwitch.collectionKeys, nodeSwitch.rows, node).has(row.key)
    : false
  return (
    <div className="bg-base">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return
          e.preventDefault()
          setOpen((o) => !o)
        }}
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left hover:bg-subtle"
      >
        <Caret className="size-3.5 shrink-0 text-fg-subtle" />
        {/* Owns its click: toggling the node is not opening it. */}
        {nodeSwitch && answerable && (
          <span
            className="flex shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              size="xsmall"
              requires={STRATEGY_REQUIRES}
              checked={nodeOn}
              onCheckedChange={(checked) => nodeSwitch.setNode(node, row.key, checked === true)}
            />
          </span>
        )}
        <span className="min-w-0 flex-1 truncate paragraph-sm text-fg-base">
          {node.groupValue === "" ? UNCATEGORIZED : node.groupValue}
        </span>
        <span className="shrink-0 paragraph-sm text-fg-muted tabular-nums">
          {`${formatNumber(variants.length)} variant${variants.length === 1 ? "" : "s"}`}
        </span>
        <span className="flex w-20 shrink-0 justify-end">
          {sizing ? <SizingSpinner className="w-16" /> : <DeltaCell value={units} />}
        </span>
        {answerLabel != null && answerable && (
          <Badge
            variant={seasonAnswer?.inherited ? "gray" : "violet"}
            size="xs"
            className="shrink-0"
            title={
              seasonAnswer?.inherited
                ? "Follows the collection's answer. Open the node to answer for it alone."
                : "This node answers on its own. Open it to change or clear that."
            }
          >
            {answerLabel}
          </Badge>
        )}
        {nodeSwitch && answerable && nodeOwn != null && (
          <Badge
            variant="violet"
            size="xs"
            className="shrink-0"
            title="This node is switched on its own, whatever the collection says. Open it to follow the collection again."
          >
            {nodeOwn ? "On here" : "Off here"}
          </Badge>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-3 border-t border-base bg-subtle px-3 py-3">
          {nodeSwitch && answerable && nodeOwn != null && (
            <div className="flex items-center justify-between gap-2">
              <span className="paragraph-sm text-fg-muted">
                {nodeOwn ? "Switched on for this node alone." : "Switched off for this node alone."}
              </span>
              <Button
                variant="transparent"
                size="xs"
                requires={STRATEGY_REQUIRES}
                onClick={() => nodeSwitch.setNode(node, row.key, null)}
              >
                Follow the collection
              </Button>
            </div>
          )}
          {answerable ? (
            <NodeInsightBody node={node} variants={variants} row={row} summary={summary} />
          ) : (
            <p className="paragraph-sm text-fg-muted">
              Variants with no value on this level follow the collection and cannot be set on their
              own.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** The comparable-collection card is the host's to render, once. */
export const SettingsQuestions = ({ summary }: { summary: SetupSummary }) => (
  <InsightSettings summary={summary} />
)

export { usePublishSetupSummary }
